/**
 * Urgency Counter — Custom Element
 * Right-edge vertical strip showing dynamic urgency messaging
 * with demand bar, sold count, and rotating messages.
 *
 * On init the strip element is moved to <body> to guarantee
 * position:fixed works regardless of ancestor overflow/transform.
 */
if (!customElements.get('urgency-counter')) {
  customElements.define(
    'urgency-counter',
    class UrgencyCounter extends HTMLElement {
      constructor() {
        super();
        this.timer = null;
        this.dismissed = false;
      }

      connectedCallback() {
        // Defer initialization: connectedCallback fires before children
        // are parsed, so querySelector would return null. We wait one
        // frame to let the parser finish creating child elements.
        if (this._initialized) return;
        var self = this;
        requestAnimationFrame(function() {
          self._setup();
        });
      }

      _setup() {
        this.strip = this.querySelector('#urgencyCard');
        this.numberEl = this.querySelector('#urgencyNumber');
        this.textEl = this.querySelector('#urgencyText');
        this.closeBtn = this.querySelector('#urgencyClose');
        this.barEl = this.querySelector('#urgencyBar');
        this.barLabel = this.querySelector('#urgencyBarLabel');

        if (!this.strip || !this.numberEl || !this.textEl) return;
        if (this._initialized) return;
        this._initialized = true;

        this.minSold = parseInt(this.dataset.minSold) || 3;
        this.maxSold = parseInt(this.dataset.maxSold) || 18;
        this.timeWindow = parseInt(this.dataset.timeWindow) || 30;
        this.updateInterval = (parseInt(this.dataset.updateInterval) || 25) * 1000;
        var rawDelay = this.dataset.showDelay;
        this.showDelay = (rawDelay !== undefined && rawDelay !== '' ? parseInt(rawDelay) : 0) * 1000;

        // Parse custom messages
        var rawMessages = this.dataset.messages || '';
        this.messages = rawMessages
          ? rawMessages.split(',').map(function(m) { return m.trim(); }).filter(Boolean)
          : [];

        // Default urgency-focused messages
        if (this.messages.length === 0) {
          this.messages = [
            'sold in last ' + this.timeWindow + ' min',
            'people buying now',
            'orders today — selling fast!',
            'grabbed this — hurry!',
            'bought recently'
          ];
        }

        this.demandLabels = ['High demand', 'Very high demand', 'Selling fast!', 'Almost gone!'];

        // Move the strip to <body> so fixed positioning isn't affected by ancestors
        this.moveToBody();

        this.init();
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
      }

      moveToBody() {
        if (this.strip && this.strip.parentElement !== document.body) {
          document.body.appendChild(this.strip);
        }
      }

      init() {
        var self = this;

        // Close button
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', function() { self.dismiss(); });
        }

        // Set initial values
        this.updateValues();

        // Show after delay (0 = immediately)
        if (this.showDelay <= 0) {
          // Use a minimal timeout to let the DOM settle after moveToBody
          setTimeout(function() {
            if (!self.dismissed && self.strip) {
              self.strip.classList.add('visible');
            }
          }, 50);
        } else {
          setTimeout(function() {
            if (!self.dismissed && self.strip) {
              self.strip.classList.add('visible');
            }
          }, this.showDelay);
        }

        // Periodic updates
        this.timer = setInterval(function() {
          if (!self.dismissed) {
            self.animateUpdate();
          }
        }, this.updateInterval);
      }

      getRandomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
      }

      updateValues() {
        var count = this.getRandomInt(this.minSold, this.maxSold);
        var message = this.messages[this.getRandomInt(0, this.messages.length - 1)];

        this.numberEl.textContent = count;
        this.textEl.textContent = message;

        // Update demand bar — always show high urgency (60–100%)
        if (this.barEl) {
          var percent = Math.min(100, Math.max(60, this.getRandomInt(65, 98)));
          this.barEl.style.width = percent + '%';

          // Update demand label — all labels convey urgency
          if (this.barLabel) {
            var labelIdx;
            if (percent < 72) labelIdx = 0;
            else if (percent < 82) labelIdx = 1;
            else if (percent < 92) labelIdx = 2;
            else labelIdx = 3;
            this.barLabel.textContent = this.demandLabels[labelIdx];
          }
        }
      }

      animateUpdate() {
        var self = this;

        this.strip.classList.add('hiding');

        setTimeout(function() {
          self.updateValues();
          self.strip.classList.remove('hiding');

          self.numberEl.classList.add('bounce');
          setTimeout(function() {
            self.numberEl.classList.remove('bounce');
          }, 500);
        }, 350);
      }

      dismiss() {
        this.dismissed = true;
        if (this.strip) {
          this.strip.classList.remove('visible');
          this.strip.classList.add('hiding');
        }
        if (this.timer) clearInterval(this.timer);

        var self = this;
        setTimeout(function() {
          if (self.strip) self.strip.style.display = 'none';
        }, 400);
      }
    }
  );
}
